import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import moment from 'moment-timezone'
import React, { ReactElement } from 'react'
import SingleDateInputField from 'src/components/SingleDateInputField/SingleDateInputField'
import { useTranslate } from 'src/localization/Trns'
import { KybRelationshipType } from 'src/queries/payments/payments.types'

import { KybAddressFieldset } from './KybAddressFieldset'
import { kybInvalidMessage } from './kybErrorMessages'
import { kybOwnerCardStyle } from './kybModal.style'
import {
  BeneficialOwnerErrors,
  BeneficialOwnerValues,
  digitsOnly,
} from './paymentsKyb.validation'

export interface KybOwnerCardProps {
  index: number
  owner: BeneficialOwnerValues
  onUpdate: (partial: Partial<BeneficialOwnerValues>) => void
  onRemove: () => void
  canRemove: boolean
  errors?: BeneficialOwnerErrors
}

/**
 * One beneficial owner / control person card in the KYB modal's step 2. Role is
 * a multi-select (a person can be both a control person and a beneficial owner);
 * ownership % is required when the beneficial-owner role is selected. Rebuilt
 * from the prototype's `OwnerCard` on top of the ported validation model
 * (`BeneficialOwnerValues`).
 */
const ROLE_OPTIONS: { value: KybRelationshipType; labelKey: string }[] = [
  { value: 'control_person', labelKey: 'Control person' },
  { value: 'beneficial_owner', labelKey: 'Beneficial owner' },
]

export function KybOwnerCard({
  index,
  owner,
  onUpdate,
  onRemove,
  canRemove,
  errors,
}: KybOwnerCardProps): ReactElement {
  const translate = useTranslate()

  const isBeneficialOwner = owner.relationshipTypes.includes('beneficial_owner')

  const toggleRole = (role: KybRelationshipType, checked: boolean): void => {
    const next = checked
      ? Array.from(new Set([...owner.relationshipTypes, role]))
      : owner.relationshipTypes.filter((r) => r !== role)
    onUpdate({
      relationshipTypes: next,
      ...(role === 'beneficial_owner' &&
      !checked &&
      !next.includes('beneficial_owner')
        ? { ownershipPercentage: '' }
        : {}),
    })
  }

  return (
    <div className="KybOwnerCard" css={kybOwnerCardStyle}>
      <div className="KybOwnerCard-header">
        <h3 className="KybOwnerCard-title">
          {translate('Person ${number}', { number: String(index + 1) })}
        </h3>
        {canRemove && (
          <Button
            variant="no-style"
            onClick={onRemove}
            testId={`kyb-owner-${index}-remove`}
          >
            {translate('Remove')}
          </Button>
        )}
      </div>

      <div className="KybOwnerCard-grid">
        <TextInput
          label={translate('First name')}
          requirement="required"
          value={owner.firstName}
          onChange={(e): void => onUpdate({ firstName: e.target.value })}
          testId={`kyb-owner-${index}-first-name`}
        />
        <TextInput
          label={translate('Last name')}
          requirement="required"
          value={owner.lastName}
          onChange={(e): void => onUpdate({ lastName: e.target.value })}
          testId={`kyb-owner-${index}-last-name`}
        />
        <SingleDateInputField
          id={`kyb-owner-${index}-dob`}
          fieldLabel={translate('Date of birth')}
          allowPastAndCurrentDates
          dateMoment={
            owner.dateOfBirth
              ? moment(owner.dateOfBirth, 'YYYY-MM-DD')
              : undefined
          }
          onDateMomentChange={(m): void =>
            onUpdate({ dateOfBirth: m ? m.format('YYYY-MM-DD') : '' })
          }
          errors={
            kybInvalidMessage(translate, errors?.dateOfBirth)
              ? [kybInvalidMessage(translate, errors?.dateOfBirth) as string]
              : undefined
          }
        />
        <TextInput
          label={translate('Email')}
          requirement="required"
          type="email"
          value={owner.email}
          onChange={(e): void => onUpdate({ email: e.target.value })}
          errorText={kybInvalidMessage(translate, errors?.email)}
          testId={`kyb-owner-${index}-email`}
        />
        <fieldset className="KybOwnerCard-roles span2">
          <legend className="KybOwnerCard-rolesLegend">
            <span>{translate('Role at this business')}</span>
            <span className="KybOwnerCard-required" aria-hidden="true">
              {'*'}
            </span>
          </legend>
          <p className="KybOwnerCard-rolesHelper">
            {translate('Select all that apply — a person can be both.')}
          </p>
          {ROLE_OPTIONS.map((option) => (
            <label key={option.value} className="KybOwnerCard-roleOption">
              <Checkbox
                aria-label={translate(option.labelKey)}
                checked={owner.relationshipTypes.includes(option.value)}
                isInvalid={!!errors?.relationshipTypes}
                onChange={(e): void =>
                  toggleRole(option.value, e.target.checked)
                }
                data-testid={`kyb-owner-${index}-role-${option.value}`}
              />
              <span>{translate(option.labelKey)}</span>
            </label>
          ))}
          {errors?.relationshipTypes && (
            <p className="KybOwnerCard-roleError" role="alert">
              {translate('Select at least one role.')}
            </p>
          )}
        </fieldset>
        {isBeneficialOwner && (
          <TextInput
            label={translate('Ownership % (25–100)')}
            requirement="required"
            value={owner.ownershipPercentage}
            onChange={(e): void =>
              onUpdate({ ownershipPercentage: e.target.value })
            }
            errorText={kybInvalidMessage(
              translate,
              errors?.ownershipPercentage,
            )}
            testId={`kyb-owner-${index}-ownership`}
          />
        )}
        <TextInput
          label={translate('Social Security Number (SSN)')}
          requirement="required"
          // Mask the digits (shoulder-surfing) and keep them out of browser
          // autofill/history. inputMode surfaces the numeric keypad. A dedicated
          // masked/reveal control is a follow-up (ext-ui TextInput has none yet).
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={owner.ssn}
          maxLength={9}
          onChange={(e): void =>
            onUpdate({ ssn: digitsOnly(e.target.value).slice(0, 9) })
          }
          errorText={kybInvalidMessage(translate, errors?.ssn)}
          testId={`kyb-owner-${index}-ssn`}
        />
        <TextInput
          label={translate('Phone')}
          requirement="required"
          value={owner.phoneNumber}
          onChange={(e): void => onUpdate({ phoneNumber: e.target.value })}
          errorText={kybInvalidMessage(translate, errors?.phoneNumber)}
          testId={`kyb-owner-${index}-phone`}
        />
      </div>

      <KybAddressFieldset
        legend={translate('Home address')}
        idPrefix={`kyb-owner-${index}-address`}
        value={owner.address}
        onChange={(address): void => onUpdate({ address })}
        errors={errors?.address}
      />
    </div>
  )
}
